/**
 * 租户路由配置
 * 为租户用户提供专属路由，使用 TenantLayout 作为布局
 */

import React, { lazy, Suspense } from 'react';
import { RouteObject, Outlet, Navigate } from 'react-router-dom';
import { Spin } from 'antd';
import TenantLayout from '@/layouts/TenantLayout';
import AdminModeRoute from './guards/AdminModeRoute';
import PermissionRoute from './guards/PermissionRoute';
import CustomerAdminBlockedRoute from './guards/CustomerAdminBlockedRoute';
// 小程序管理：分销/项目 小程序骨架占位页（非懒加载，体量小且多路由复用）
import MiniProgramPlaceholder from '@/pages/MiniProgram/Placeholder';

// 租户工作台
const TenantDashboard = lazy(() => import('@/pages/tenant/Dashboard'));
const ExportTaskList = lazy(() => import('@/modules/business/pages/Task/ExportTaskList'));
const OrderImportTaskList = lazy(() => import('@/modules/business/pages/Task/OrderImportTaskList'));
const ScheduleCenter = lazy(() => import('@/modules/business/pages/Task/ScheduleCenter'));

// 商品管理页面（从modules导入）
// REQ-20260621-0001: 商品中心 Tab 容器（商品列表/SKU/品牌/标签/分类）
const ProductCenterPage = lazy(() => import('@/modules/business/pages/ProductManagement/ProductCenterPage'));
// REQ-20260621-0007: 商品配置中心 Tab 容器（模板/起售/起运/价格监控/白名单/组合/推广）
const ProductConfigPage = lazy(() => import('@/modules/business/pages/ProductManagement/ProductConfigPage'));
const ProductFormPage = lazy(() => import('@/modules/business/pages/ProductManagement/ProductFormPage'));
const ProductDetailPage = lazy(() => import('@/modules/business/pages/ProductManagement/ProductDetailPage'));
const SkuManagerPage = lazy(() => import('@/modules/business/pages/ProductManagement/SkuManagerPage'));
const PromotionProductOption = lazy(() => import('@/modules/business/pages/PromotionPlan/PromotionProductOption'));
const PromotionPlanFormulate = lazy(() => import('@/modules/business/pages/PromotionPlan/PromotionPlanFormulate'));
const PromotionPlanAudit = lazy(() => import('@/modules/business/pages/PromotionPlan/PromotionPlanAudit'));

// SKU独立管理页面
const SkuListPage = lazy(() => import('@/modules/business/pages/SkuManagement/SkuListPage'));
const SkuFormPage = lazy(() => import('@/modules/business/pages/SkuManagement/SkuFormPage'));
const StockLogsPage = lazy(() => import('@/modules/business/pages/SkuManagement/StockLogsPage'));
const SkuDetailPage = lazy(() => import('@/modules/business/pages/SkuManagement/SkuDetailPage'));

// 商品配置页面
const WhitelistManagerPage = lazy(() => import('@/modules/business/pages/ProductManagement/WhitelistManagerPage'));
const StartingQuantityPage = lazy(() => import('@/modules/business/pages/ProductManagement/StartingQuantityPage'));
const SaleRegionPage = lazy(() => import('@/modules/business/pages/ProductManagement/SaleRegionPage'));
const CustomSkuConfigPage = lazy(() => import('@/modules/business/pages/ProductManagement/CustomSkuConfigPage'));
const PriceExceptionPage = lazy(() => import('@/modules/business/pages/ProductManagement/PriceExceptionPage'));

// 商品分类管理页面（使用 CategoryManager 组件 + API wrapper）
const CategoryFormPage = lazy(() => import('@/modules/business/pages/Product/CategoryForm'));

// 商品品牌管理页面（使用 BrandManager 组件 + API wrapper）
const BrandFormPage = lazy(() => import('@/pages/Product/BrandForm'));

// 商品标签管理页面
const LabelFormPage = lazy(() => import('@/pages/Product/LabelForm'));

// 商品模板管理页面
const TemplateFormPage = lazy(() => import('@/pages/Product/TemplateForm'));

// 订单管理页面
const AfterSalesCenter = lazy(() => import('@/pages/Order/AfterSalesCenter'));
const SalesOrderList = lazy(() => import('@/pages/Order/SalesOrderList'));
const SalesOrderForm = lazy(() => import('@/pages/Order/SalesOrderForm'));
const SalesOrderDetail = lazy(() => import('@/pages/Order/SalesOrderDetail'));
const PendingApprovalList = lazy(() => import('@/pages/Order/PendingApprovalList'));
const PurchaseOrderList = lazy(() => import('@/pages/Order/PurchaseOrderList'));
const PurchaseOrderForm = lazy(() => import('@/pages/Order/PurchaseOrderForm'));
const PurchaseOrderDetail = lazy(() => import('@/pages/Order/PurchaseOrderDetail'));
const PreOrderForm = lazy(() => import('@/pages/Order/PreOrderForm'));
const CheckoutPage = lazy(() => import('@/pages/Order/CheckoutPage'));

// 退货单管理页面（保留旧组件引用）
const ReturnOrderDetail = lazy(() => import('@/pages/Order/ReturnOrderDetail'));
const CreateReturnOrder = lazy(() => import('@/pages/Order/CreateReturnOrder'));

// 售后单

// 付款记录页面
const PaymentRecordList = lazy(() => import('@/pages/Order/PaymentRecordList'));
const PaymentRecordDetail = lazy(() => import('@/pages/Order/PaymentRecordDetail'));

// 退款申请页面

// 采购单统计页面

// 购物车页面
const CartPage = lazy(() => import('@/pages/Cart/CartPage'));

// 客户管理页面
const CustomerList = lazy(() => import('@/pages/Customer/CustomerList'));
// 客户中心收敛 11→3：客户档案 Tab 容器（列表/分类/等级/销售员/销售关系）
const CustomerProfileCenter = lazy(() => import('@/pages/Customer/CustomerProfileCenter'));
// 客户审核页面（仅顶级租户可见）
const CustomerReview = lazy(() => import('@/pages/Customer/CustomerReview'));
const CustomerDetailPage = lazy(() => import('@/pages/Customer/CustomerDetail'));
const YCodeDetailPage = lazy(() => import('@/pages/Customer/YCodeManagement/YCodeDetail'));
const CustomerCategory = lazy(() => import('@/pages/Customer/CustomerCategory'));
const CustomerQuoteList = lazy(() => import('@/pages/Customer/CustomerQuotes'));
const CustomerQuoteForm = lazy(() => import('@/pages/Customer/CustomerQuoteForm'));
const CustomerQuoteView = lazy(() => import('@/pages/Customer/CustomerQuoteView'));
const CustomerPriceList = lazy(() => import('@/pages/Customer/CustomerPrices'));
const CustomerPriceForm = lazy(() => import('@/pages/Customer/CustomerPriceForm'));
const CustomerProducts = lazy(() => import('@/pages/Customer/CustomerProducts'));
// REQ-20260622-0011: 客户中心 Tab 容器（渠道管理/分销空壳并入；分销→货盘不变）
const CustomerTradeCenter = lazy(() => import('@/pages/Customer/CustomerTradeCenter'));
// 客户中心收敛 7→6：渠道与Y码 合并 Tab 容器（网店/渠道/Y码/Y码价格）
const CustomerChannelYcodeCenter = lazy(() => import('@/pages/Customer/CustomerChannelYcodeCenter'));
const ThresholdConfigPage = lazy(() => import('@/pages/Customer/ThresholdConfig'));
const MessageSubscriptionsPage = lazy(() => import('@/pages/Customer/MessageSubscriptions'));

// 客户销售系统页面 (Phase 1.1)
const SalesmanList = lazy(() => import('@/modules/business/pages/Customer/SalesmanList'));
const CustomerLevelList = lazy(() => import('@/modules/business/pages/Customer/CustomerLevelList'));
const LevelPriceList = lazy(() => import('@/modules/business/pages/Customer/LevelPriceList'));
const CustomerSalesRelation = lazy(() => import('@/modules/business/pages/Customer/CustomerSalesRelation'));

// Y码直客系统页面 (Phase 4.1)
const YcodeManagement = lazy(() => import('@/modules/business/pages/Ycode/YcodeManagement'));
const YcodeRedemptionList = lazy(() => import('@/modules/business/pages/Ycode/YcodeRedemptionList'));

// 草稿单管理页面（订单中心，采购草稿 status=10）
const DraftOrderList = lazy(() => import('@/pages/Order/DraftOrderList'));

// 货盘管理系统页面 (Phase 4.2)
const CargoLotList = lazy(() => import('@/modules/business/pages/CargoLot/CargoLotList'));
const CargoLotForm = lazy(() => import('@/modules/business/pages/CargoLot/CargoLotForm'));
const CargoAllocationManagement = lazy(() => import('@/modules/business/pages/CargoLot/CargoAllocationManagement'));

// 产品白名单系统页面 (Phase 2.1)

// 产品组合方案系统页面 (Phase 2.2)

// 推广项目管理系统页面 (Phase 2.3)

// 结算单管理系统页面 (Phase 3.1)
const SettlementBillDetail = lazy(() => import('@/modules/finance/pages/SettlementBills/SettlementBillDetail'));
// REQ-20260622-0005: 结算中心 Tab 容器（结算单/结算列表/授信额度/结算规则）
const SettlementCenter = lazy(() => import('@/pages/Settlement/SettlementCenter'));

// 快递物流管理系统页面 (Phase 3.2)
const ShipmentList = lazy(() => import('@/modules/logistics/pages/Shipments/ShipmentList'));
const ShipmentDetail = lazy(() => import('@/modules/logistics/pages/Shipments/ShipmentDetail'));
const ShippingCarrierList = lazy(() => import('@/modules/logistics/pages/Carriers/ShippingCarrierList'));
const CarrierPermissionList = lazy(() => import('@/modules/logistics/pages/CarrierPermissions/CarrierPermissionList'));

// 仓库管理页面
const WarehouseListPage = lazy(() => import('@/pages/Warehouse/WarehouseList'));
const InventoryListPage = lazy(() => import('@/pages/Warehouse/InventoryList'));
const InventoryLogsPage = lazy(() => import('@/modules/business/pages/Inventory/InventoryLogsPage'));
const InboundManagement = lazy(() => import('@/pages/Warehouse/InboundManagement'));
const OutboundManagement = lazy(() => import('@/pages/Warehouse/OutboundManagement'));
const StocktakeManagement = lazy(() => import('@/pages/Warehouse/StocktakeManagement'));
const WarehouseReceiptListPage = lazy(() => import('@/pages/Warehouse/ReceiptList'));
const WarehouseReceiptFormPage = lazy(() => import('@/pages/Warehouse/ReceiptForm'));
const WarehouseReceiptDetailPage = lazy(() => import('@/pages/Warehouse/ReceiptDetail'));
const WarehousePermissionsPage = lazy(() => import('@/pages/Warehouse/WarehousePermissions'));
const WarehouseCarrierConfigPage = lazy(() => import('@/pages/Warehouse/WarehouseCarrierConfig'));

// 协同仓页面
const CollabShareInventoryPage = lazy(() => import('@/pages/Collab/ShareInventory'));
const CollabSourceOrderPage = lazy(() => import('@/pages/Collab/SourceOrder'));
const CollabConfigPage = lazy(() => import('@/pages/Collab/Config'));

// 财务管理页面
const AccountListPage = lazy(() => import('@/pages/Finance/AccountList'));
const BalanceListPage = lazy(() => import('@/pages/Finance/BalanceList'));
const InvoiceListPage = lazy(() => import('@/pages/Finance/InvoiceList'));
const AdminInvoiceListPage = lazy(() => import('@/pages/Finance/AdminInvoiceList'));
const InvoiceApplyPage = lazy(() => import('@/pages/Finance/InvoiceApply'));
const InvoiceTitleListPage = lazy(() => import('@/pages/Finance/InvoiceTitleList'));
const ReconciliationListPage = lazy(() => import('@/pages/Finance/ReconciliationList'));
const ReceiptListPage = lazy(() => import('@/pages/Finance/ReceiptList'));
const DisbursementListPage = lazy(() => import('@/pages/Finance/DisbursementList'));
const FundAccountListPage = lazy(() => import('@/pages/Finance/FundAccountList'));

// 结算管理页面

// 营销管理页面
const PromotionListPage = lazy(() => import('@/pages/Promotion/PromotionList'));
const CouponListPage = lazy(() => import('@/pages/Promotion/CouponList'));

// 渠道管理页面（REQ-20260511-0030）
const ChannelShopListPage = lazy(() => import('@/pages/Channel/ShopList'));
const ChannelListPage = lazy(() => import('@/pages/Channel/ChannelList'));

// 系统管理补充页面
const DataDictionaryPage = lazy(() => import('@/pages/System/DataDictionary'));
const SystemConfigPage = lazy(() => import('@/pages/System/SystemConfigPage'));
const LnYimaConfigPage = lazy(() => import('@/pages/System/LnYimaConfig'));

// 物流/运费管理页面
const LogisticsCompanyListPage = lazy(() => import('@/pages/Shipping/LogisticsCompanyList'));
const FreightTemplateListPage = lazy(() => import('@/pages/Shipping/FreightTemplateList'));

// 配送管理页面
const DeliveryListPage = lazy(() => import('@/pages/Delivery/DeliveryList'));

// 报表页面
// REQ-20260622-0012: 订单经营分析（整合订单统计+销售报表）
const OrderBusinessAnalysis = lazy(() => import('@/pages/Report/OrderBusinessAnalysis'));
const InventoryReportPage = lazy(() => import('@/pages/Report/InventoryReport'));
const FinanceReportPage = lazy(() => import('@/pages/Report/FinanceReport'));

// 客户关系管理页面 (Business)
const CustomerManagement = lazy(() => import('@/pages/Business/CustomerManagement'));

// 管理模式页面
const ThemeConfigPage = lazy(() => import('@/pages/Admin/ThemeConfig'));
const SubTenantsPage = lazy(() => import('@/pages/Admin/SubTenants'));
const TenantSettingsPage = lazy(() => import('@/pages/Admin/TenantSettings'));
// REQ-20260508-0001 #10186: 租户品牌设置（登录页主题）
const BrandSettingsPage = lazy(() => import('@/pages/Settings/Brand'));
// T88-1: 团购小程序后台管理
const TuangouPackagesPage = lazy(() => import('@/pages/Admin/TuangouPackages'));
// PR #377: 套餐详情查看页（SPU-based，含小程序预览）
const TuangouPackageDetailPage = lazy(() => import('@/pages/Admin/TuangouPackages/PackageDetail'));
const TuangouSettingsPage = lazy(() => import('@/pages/Admin/TuangouSettings'));
// T88-2: 团购小程序后台管理扩展
const TuangouPromoSkusPage = lazy(() => import('@/pages/Admin/TuangouPromoSkus'));
const TuangouOrdersPage = lazy(() => import('@/pages/Admin/TuangouOrders'));
const TuangouCustomersPage = lazy(() => import('@/pages/Admin/TuangouCustomers'));
const TuangouLeadsPage = lazy(() => import('@/pages/Admin/TuangouLeads'));
const TuangouRefundRequestsPage = lazy(() => import('@/pages/Admin/TuangouRefundRequests'));
// task #101: 团购小程序图文内容块管理
const TuangouContentBlocksPage = lazy(() => import('@/pages/Admin/TuangouContentBlocks'));
// Task D #133: 团购需求管理
const TuangouDemandsPage = lazy(() => import('@/pages/Admin/TuangouDemands'));
// TASK-19 M4: 经销商商城小程序配置（ADR-0002 app_scope='mall'）
const MallSettingsPage = lazy(() => import('@/pages/Admin/MallSettings'));

// 订阅与计费页面（管理模式）
const MySubscriptionPage = lazy(() => import('@/pages/Admin/Subscription/MySubscription'));
const MyOrdersPage = lazy(() => import('@/pages/Admin/Subscription/MyOrders'));
const MyInvoicesPage = lazy(() => import('@/pages/Admin/Subscription/MyInvoices'));
const PaymentPage = lazy(() => import('@/pages/Admin/Subscription/Payment'));

// 订阅自服务页面（模块控制）
const SubSelfMyPlanPage = lazy(() => import('@/pages/Subscription/MyPlan'));
const SubSelfOrderListPage = lazy(() => import('@/pages/Subscription/OrderList'));
const SubSelfInvoiceListPage = lazy(() => import('@/pages/Subscription/InvoiceList'));

// 管理概览页面
const OverviewPage = lazy(() => import('@/pages/tenant/Overview'));
// 森林绿管理看板（REQ-20260618-0003 · M1）
const ForestDashboardPage = lazy(() => import('@/pages/tenant/ForestDashboard'));

// 岗位管理页面
const PositionList = lazy(() => import('@/modules/foundation/pages/Position/PositionList'));

// 数据权限管理页面
const DataScopeList = lazy(() => import('@/modules/foundation/pages/DataPermission/DataScopeList'));
const FieldPermissionList = lazy(() => import('@/modules/foundation/pages/DataPermission/FieldPermissionList'));
const DataPermissionConfigPage = lazy(() => import('@/modules/foundation/pages/DataPermission/DataPermissionConfig'));

// 日志管理页面
const LoginLogList = lazy(() => import('@/modules/foundation/pages/Logs/LoginLogList'));
const AuditLogList = lazy(() => import('@/modules/foundation/pages/Logs/AuditLogList'));

// 消息中心页面
const MessageCenterPage = lazy(() => import('@/modules/foundation/pages/MessageCenter/MessageCenterPage'));
const AnnouncementManagementPage = lazy(() => import('@/modules/foundation/pages/MessageCenter/AnnouncementManagementPage'));

// 权限引擎页面
const PermissionOverridePage = lazy(() => import('@/modules/foundation/pages/PermissionEngine/PermissionOverridePanel'));
const PermissionAuditLogPage = lazy(() => import('@/modules/foundation/pages/PermissionEngine/PermissionAuditLog'));

// 用户管理页面
const UserListPage = lazy(() => import('@/modules/foundation/pages/User/UserList'));
const OpenapiAppsPage = lazy(() => import('@/pages/System/OpenapiApps'));
const CustomerScopeOpenapiAppsPage = lazy(() => import('@/pages/System/OpenapiApps/CustomerScope'));
const IntegrationConfigsPage = lazy(() => import('@/pages/System/IntegrationConfigs'));
// 聚水潭对接页面
const JushuitanPage = lazy(() => import('@/modules/external/pages/JST/JushuitanPage'));
// 客户视角聚水潭页面（Tab 页签：商品管理 / 订单管理）
const JstCustomerPage = lazy(() => import('@/modules/external/pages/JST/customer/JstCustomerPage'));
const JobListPage = lazy(() => import('@/pages/System/JobList'));
const JobLogListPage = lazy(() => import('@/pages/System/JobLogList'));
const UserDetailPage = lazy(() => import('@/modules/foundation/pages/User/UserDetail'));

// 角色管理页面
const RoleListPage = lazy(() => import('@/modules/foundation/pages/Role/RoleList'));

// 权限资源管理页面
const PermissionResourcePage = lazy(() => import('@/modules/foundation/pages/Permission/PermissionResourceList'));

// 菜单管理页面
const MenuManagementPage = lazy(() => import('@/modules/foundation/pages/Menu/MenuManagement'));

// 组织管理增强页面
const OrganizationManagementPage = lazy(() => import('@/modules/foundation/pages/Organization/OrganizationManagement'));

// 权限溯源页面
const PermissionTracingPage = lazy(() => import('@/modules/foundation/pages/PermissionEngine/PermissionTracingPage'));

// 原型预览页面
const SecurityAuditPrototype = lazy(() => import('@/modules/foundation/pages/Prototype/SecurityAuditPrototype'));
const AccessControlPrototype = lazy(() => import('@/modules/foundation/pages/Prototype/AccessControlPrototype'));
const SystemSettingsPrototype = lazy(() => import('@/modules/foundation/pages/Prototype/SystemSettingsPrototype'));
const OrganizationPrototype = lazy(() => import('@/modules/foundation/pages/Prototype/OrganizationPrototype'));

// 交易管理页面 (REQ-20260228-0001)
const CustomerOrders = lazy(() => import('@/pages/Trade/CustomerOrders'));
const CommissionSettlement = lazy(() => import('@/pages/Trade/CommissionSettlement'));

// 分销管理页面 (REQ-1005 更名)
const SubTenantListPage = lazy(() => import('@/modules/distribution/pages/SubTenant/SubTenantListPage'));
const SubTenantFormPage = lazy(() => import('@/modules/distribution/pages/SubTenant/SubTenantFormPage'));
const SubTenantDetailPage = lazy(() => import('@/modules/distribution/pages/SubTenant/SubTenantDetailPage'));
const ProductAssignPage = lazy(() => import('@/modules/distribution/pages/Product/ProductAssignPage'));
const DiscountStrategyPage = lazy(() => import('@/modules/distribution/pages/Pricing/DiscountStrategyPage'));

// 账号设置页面
const AccountSettings = lazy(() => import('@/pages/Account/Settings'));

// 个人中心页面
const Profile = lazy(() => import('@/pages/Account/Profile'));

// 统一的加载中内容
const loadingFallback = (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    minHeight: 200
  }}>
    <Spin size="large" tip="加载中..."><div style={{ minHeight: 100 }} /></Spin>
  </div>
);

// 包裹懒加载组件的Suspense高阶函数
const withSuspense = (Component: React.LazyExoticComponent<React.ComponentType>) => (
  <Suspense fallback={loadingFallback}>
    <Component />
  </Suspense>
);

/**
 * 租户路由配置
 * 这些路由需要登录认证，使用 TenantLayout 布局
 */
export const tenantRoutes: RouteObject = {
  path: '/tenant',
  element: <TenantLayout />,
  children: [
    // 租户工作台
    {
      path: 'dashboard',
      element: withSuspense(TenantDashboard),
    },

    // 导出任务中心(菜单 /tenant/task/export;REQ-20260721-0005)
    {
      path: 'task/export',
      element: withSuspense(ExportTaskList),
    },

    // 导入任务中心(菜单 /tenant/task/import;REQ-20260722-0014 阶段3)
    {
      path: 'task/import',
      element: withSuspense(OrderImportTaskList),
    },

    // 通用调度中心（任务配置 + 执行记录）
    {
      path: 'task/schedules',
      element: withSuspense(ScheduleCenter),
    },

    // ==================== 商品管理 ====================
    {
      path: 'productOption',
      element: (
        <PermissionRoute permissions={['tenant:promotion-plan:view']}>
          {withSuspense(PromotionProductOption)}
        </PermissionRoute>
      ),
    },
    {
      path: 'prd_combination_formulate',
      element: (
        <PermissionRoute permissions={['tenant:promotion-plan:view']}>
          {withSuspense(PromotionPlanFormulate)}
        </PermissionRoute>
      ),
    },
    {
      path: 'prd_combination_audit',
      element: (
        <PermissionRoute permissions={['tenant:promotion-plan:audit']}>
          {withSuspense(PromotionPlanAudit)}
        </PermissionRoute>
      ),
    },
    {
      path: 'product',
      children: [
        // REQ-20260621-0001 M1: /tenant/product 与 /tenant/product/list 均渲染商品中心 Tab 容器
        { index: true, element: <Navigate to="/tenant/product/list" replace /> },
        { path: 'list', element: withSuspense(ProductCenterPage) },
        // REQ-20260621-0007: 商品配置中心
        { path: 'config', element: withSuspense(ProductConfigPage) },
        { path: 'add', element: withSuspense(ProductFormPage) },
        { path: 'edit/:id', element: withSuspense(ProductFormPage) },
        { path: 'detail/:id', element: withSuspense(ProductDetailPage) },
        // SKU 管理 Tab 已移除，旧入口回到商品列表；单商品 SKU 管理路由继续保留
        { path: 'sku', element: <Navigate to="/tenant/product/list" replace /> },
        { path: 'sku/:spuId', element: withSuspense(SkuManagerPage) },
        // 商品配置功能
        { path: 'whitelist/:productId', element: withSuspense(WhitelistManagerPage) },
        { path: 'whitelist-config', element: <Navigate to="/tenant/product/config?tab=whitelist" replace /> },
        { path: 'bundle-config', element: <Navigate to="/tenant/product/config" replace /> },
        { path: 'promotion-project-config', element: <Navigate to="/tenant/product/config" replace /> },
        { path: 'starting-quantity/:productId', element: withSuspense(StartingQuantityPage) },
        { path: 'sale-region/:productId', element: withSuspense(SaleRegionPage) },
        { path: 'sku/:skuId/custom-config', element: withSuspense(CustomSkuConfigPage) },
        { path: 'price-exception/:productId', element: withSuspense(PriceExceptionPage) },
        { path: 'price-monitor', element: <Navigate to="/tenant/product/config?tab=pricemon" replace /> },
        { path: 'online-price/:productId', element: withSuspense(PriceExceptionPage) },
        // 商品分类管理
        { path: 'category', element: <Navigate to="/tenant/product/list?tab=category" replace /> },
        { path: 'category/add', element: withSuspense(CategoryFormPage) },
        { path: 'category/edit/:id', element: withSuspense(CategoryFormPage) },
        // 商品品牌管理
        { path: 'brand', element: <Navigate to="/tenant/product/list?tab=brand" replace /> },
        { path: 'brand/add', element: withSuspense(BrandFormPage) },
        { path: 'brand/edit/:id', element: withSuspense(BrandFormPage) },
        // 商品标签管理
        { path: 'label', element: <Navigate to="/tenant/product/list?tab=label" replace /> },
        { path: 'label/add', element: withSuspense(LabelFormPage) },
        { path: 'label/edit/:id', element: withSuspense(LabelFormPage) },
        // 模板管理 Tab 已移除，旧列表入口回到商品配置；新增/编辑路由继续保留
        { path: 'template', element: <Navigate to="/tenant/product/config" replace /> },
        { path: 'template/add', element: withSuspense(TemplateFormPage) },
        { path: 'template/edit/:id', element: withSuspense(TemplateFormPage) },
        // 起售模板管理
        { path: 'moq-templates', element: <Navigate to="/tenant/product/config?tab=moq" replace /> },
        // 快递起运模版管理
        { path: 'category-shipment-templates', element: <Navigate to="/tenant/product/config?tab=freight" replace /> },
      ],
    },

    // ==================== SKU管理 ====================
    {
      path: 'sku',
      element: <Outlet />,
      children: [
        { path: 'list', element: withSuspense(SkuListPage) },
        { path: 'add', element: withSuspense(SkuFormPage) },
        { path: 'edit/:id', element: withSuspense(SkuFormPage) },
        { path: ':id', element: withSuspense(SkuDetailPage) },
        { path: ':id/stock-logs', element: withSuspense(StockLogsPage) },
      ],
    },

    // ==================== 购物车 ====================
    {
      path: 'cart',
      element: withSuspense(CartPage),
    },

    // ==================== 订单管理 ====================
    {
      path: 'order',
      element: <Outlet />,
      children: [
        // REQ-20260622-0002: 业务订单(mock 旧 demo 页)下线，→ 采购订单
        { path: 'business', element: <Navigate to="/tenant/order/purchase" replace /> },
        { path: 'sales', element: withSuspense(SalesOrderList) },
        { path: 'sales/add', element: withSuspense(SalesOrderForm) },
        { path: 'sales/edit/:id', element: withSuspense(SalesOrderDetail) },
        { path: 'pending-approval', element: withSuspense(PendingApprovalList) },
        { path: 'purchase', element: withSuspense(PurchaseOrderList) },
        // 草稿单管理（采购草稿 status=10，从客户中心迁入订单中心）
        { path: 'draft-orders', element: withSuspense(DraftOrderList) },
        { path: 'purchase/add', element: withSuspense(PurchaseOrderForm) },
        { path: 'purchase/edit/:id', element: withSuspense(PurchaseOrderForm) },
        { path: 'purchase/:id', element: withSuspense(PurchaseOrderDetail) },
        { path: 'preorder/add', element: withSuspense(PreOrderForm) },
        { path: 'preorder/edit/:id', element: withSuspense(PreOrderForm) },
        { path: 'checkout', element: withSuspense(CheckoutPage) },
        // 售后单 Tab 容器（采购售后/销售售后）
        { path: 'after-sales-center', element: withSuspense(AfterSalesCenter) },
        // 退货单列表入口已下线；create/detail 子路由保留兼容历史链接
        { path: 'return', element: <Navigate to="/tenant/order/after-sales-center" replace /> },
        { path: 'return/create', element: withSuspense(CreateReturnOrder) },
        { path: 'return/:id', element: withSuspense(ReturnOrderDetail) },
        // 售后单列表 302 → 售后中心对应 Tab
        { path: 'after-sales', element: <Navigate to="/tenant/order/after-sales-center?tab=purchase" replace /> },
        { path: 'sales-after-sales', element: <Navigate to="/tenant/order/after-sales-center?tab=sales" replace /> },
        // 付款记录
        { path: 'payment', element: withSuspense(PaymentRecordList) },
        { path: 'payment/:id', element: withSuspense(PaymentRecordDetail) },
        // 退款申请列表入口已下线，旧链接回到售后单
        { path: 'refund', element: <Navigate to="/tenant/order/after-sales-center" replace /> },
        // 统计报表
        // REQ-20260622-0012: 订单统计 → 订单经营分析（报表中心）
        { path: 'statistics', element: <Navigate to="/tenant/report/order-analysis" replace /> },
      ],
    },

    // ==================== 客户管理 ====================
    {
      path: 'customer',
      element: <Outlet />,
      children: [
        // 客户中心收敛 11→3：客户档案 Tab 容器（默认入口）
        { path: 'profile', element: withSuspense(CustomerProfileCenter) },
        { path: 'list', element: withSuspense(CustomerList) },
        // 客户审核（仅顶级租户可见）
        { path: 'review', element: withSuspense(CustomerReview) },
        { path: ':id', element: withSuspense(CustomerDetailPage) },
        { path: 'category', element: withSuspense(CustomerCategory) },
        // REQ-20260622-0011: 客户中心三个 Tab 容器（客户交易/渠道/Y码）
        { path: 'trade-center', element: withSuspense(CustomerTradeCenter) },
        // 收敛 7→6：渠道与Y码 合并容器（新入口）；旧 channel-center/ycode-center 302 重定向到对应 Tab
        { path: 'channel-ycode', element: withSuspense(CustomerChannelYcodeCenter) },
        { path: 'threshold-config', element: withSuspense(ThresholdConfigPage) },
        { path: 'openapi-apps', element: withSuspense(CustomerScopeOpenapiAppsPage) },
        { path: 'message-subscriptions', element: <Navigate to="/tenant/subscription/message-subscriptions" replace /> },
        { path: 'channel-center', element: <Navigate to="/tenant/customer/channel-ycode?tab=shops" replace /> },
        { path: 'ycode-center', element: <Navigate to="/tenant/customer/channel-ycode?tab=codes" replace /> },
        // 重定向：客户报价已迁移到交易管理
        { path: 'quotes', element: <Navigate to="/tenant/trade/quotes" replace /> },
        { path: 'quotes/add', element: <Navigate to="/tenant/trade/quotes/add" replace /> },
        { path: 'quotes/edit/:id', element: <Navigate to="/tenant/trade/quotes/edit/:id" replace /> },
        // 重定向：客户价格将合入定价策略（Phase 3 完成后启用）
        { path: 'prices', element: withSuspense(CustomerPriceList) },
        { path: 'prices/add', element: withSuspense(CustomerPriceForm) },
        { path: 'prices/edit/:id', element: withSuspense(CustomerPriceForm) },
        // 重定向：客户商品将合入商品铺货（Phase 3 完成后启用）
        { path: 'products', element: withSuspense(CustomerProducts) },
        // 客户销售系统 (Phase 1.1)
        { path: 'salesman', element: withSuspense(SalesmanList) },
        { path: 'levels', element: withSuspense(CustomerLevelList) },
        { path: 'level-prices', element: withSuspense(LevelPriceList) },
        { path: 'sales-relations', element: withSuspense(CustomerSalesRelation) },
        // Y码直客分组（REQ-20260609-0009）— REQ-20260622-0011: 列表 302 → 客户中心 Y码 Tab
        { path: 'y-codes', element: <Navigate to="/tenant/customer/channel-ycode?tab=codes" replace /> },
        { path: 'y-codes/:id', element: withSuspense(YCodeDetailPage) },
        // Y码直客系统 (Phase 4.1)
        { path: 'ycode', element: withSuspense(YcodeManagement) },
        { path: 'ycode/redemptions', element: withSuspense(YcodeRedemptionList) },
        { path: 'ycode/prices', element: <Navigate to="/tenant/customer/channel-ycode?tab=prices" replace /> },
        // 货盘管理 — 重定向到新路径
        { path: 'cargo-lots', element: <Navigate to="/tenant/cargo/lots" replace /> },
        { path: 'cargo-lots/create', element: <Navigate to="/tenant/cargo/lots/create" replace /> },
        { path: 'cargo-lots/edit/:id', element: <Navigate to="/tenant/cargo/lots/edit/:id" replace /> },
        { path: 'cargo-allocations', element: <Navigate to="/tenant/cargo/allocations" replace /> },
      ],
    },

    // ==================== 货盘管理 (整合分销管理) ====================
    {
      path: 'cargo',
      element: (
        <PermissionRoute permissions={['cargo:manage', 'distribution:manage']}>
          <Outlet />
        </PermissionRoute>
      ),
      children: [
        // 货盘列表
        { path: 'lots', element: withSuspense(CargoLotList) },
        { path: 'lots/create', element: withSuspense(CargoLotForm) },
        { path: 'lots/edit/:id', element: withSuspense(CargoLotForm) },
        // 货盘分配
        { path: 'allocations', element: withSuspense(CargoAllocationManagement) },
        // 分销商管理（从分销管理迁入）
        { path: 'distributors', element: withSuspense(SubTenantListPage) },
        { path: 'distributors/create', element: withSuspense(SubTenantFormPage) },
        { path: 'distributors/:id', element: withSuspense(SubTenantDetailPage) },
        // 商品铺货（从分销管理迁入）
        { path: 'products', element: withSuspense(ProductAssignPage) },
        // 定价策略（从分销管理迁入）
        { path: 'pricing', element: withSuspense(DiscountStrategyPage) },
      ],
    },

    // ==================== 交易管理 (REQ-20260228-0001) ====================
    {
      path: 'trade',
      element: <Outlet />,
      children: [
        // 客户报价（从客户管理迁入）
        { path: 'quotes', element: withSuspense(CustomerQuoteList) },
        { path: 'quotes/add', element: withSuspense(CustomerQuoteForm) },
        { path: 'quotes/edit/:id', element: withSuspense(CustomerQuoteForm) },
        { path: 'quotes/view/:id', element: withSuspense(CustomerQuoteView) },
        // 客户订单（新增）
        { path: 'orders', element: withSuspense(CustomerOrders) },
        // 佣金结算（新增）
        { path: 'commission', element: withSuspense(CommissionSettlement) },
      ],
    },

    // ==================== 仓库管理 ====================
    {
      path: 'warehouse',
      element: <Outlet />,
      children: [
        { path: 'list', element: withSuspense(WarehouseListPage) },
        { path: 'inventory', element: withSuspense(InventoryListPage) },
        { path: 'inventory/logs', element: withSuspense(InventoryLogsPage) },
        { path: 'inbound', element: withSuspense(InboundManagement) },
        { path: 'outbound', element: withSuspense(OutboundManagement) },
        { path: 'stocktake', element: withSuspense(StocktakeManagement) },
        { path: 'receipt', element: withSuspense(WarehouseReceiptListPage) },
        { path: 'receipt/create', element: withSuspense(WarehouseReceiptFormPage) },
        { path: 'receipt/:id/edit', element: withSuspense(WarehouseReceiptFormPage) },
        { path: 'receipt/:id', element: withSuspense(WarehouseReceiptDetailPage) },
        { path: 'permissions', element: withSuspense(WarehousePermissionsPage) },
        { path: 'carrier-config', element: withSuspense(WarehouseCarrierConfigPage) },
      ],
    },

    // ==================== 协同仓 ====================
    {
      path: 'collab',
      element: <Outlet />,
      children: [
        { path: 'share-inventory', element: withSuspense(CollabShareInventoryPage) },
        { path: 'source-order', element: withSuspense(CollabSourceOrderPage) },
        { path: 'config', element: withSuspense(CollabConfigPage) },
      ],
    },

    // ==================== 财务管理 ====================
    {
      path: 'finance',
      element: <Outlet />,
      children: [
        { path: 'account', element: withSuspense(AccountListPage) },
        { path: 'balance', element: withSuspense(BalanceListPage) },
        { path: 'invoice', element: withSuspense(InvoiceListPage) },
        { path: 'admin-invoice', element: withSuspense(AdminInvoiceListPage) },
        { path: 'invoice/apply', element: withSuspense(InvoiceApplyPage) },
        { path: 'invoice/title', element: withSuspense(InvoiceTitleListPage) },
        { path: 'reconciliation', element: withSuspense(ReconciliationListPage) },
        { path: 'receipt', element: withSuspense(ReceiptListPage) },
        { path: 'disbursement', element: withSuspense(DisbursementListPage) },
        { path: 'fund-account', element: withSuspense(FundAccountListPage) },
        // REQ-20260622-0005: 结算中心 Tab 容器（结算管理并入财务）
        { path: 'settlement-center', element: withSuspense(SettlementCenter) },
        // Phase 3.1: 结算单管理（列表 302 → 结算中心 Tab；detail 保留）
        { path: 'settlement-bills', element: <Navigate to="/tenant/finance/settlement-center?tab=bills" replace /> },
        { path: 'settlement-bills/:id', element: withSuspense(SettlementBillDetail) },
      ],
    },

    // ==================== 结算管理（并入财务·结算中心，旧路由 302 重定向）====================
    {
      path: 'settlement',
      element: <Outlet />,
      children: [
        { path: 'list', element: <Navigate to="/tenant/finance/settlement-center?tab=list" replace /> },
        { path: 'credit-line', element: <Navigate to="/tenant/finance/settlement-center?tab=credit" replace /> },
        { path: 'rule', element: <Navigate to="/tenant/finance/settlement-center?tab=rule" replace /> },
      ],
    },

    // ==================== 营销管理 ====================
    {
      path: 'promotion',
      element: <Outlet />,
      children: [
        { path: 'list', element: withSuspense(PromotionListPage) },
        { path: 'coupon', element: withSuspense(CouponListPage) },
      ],
    },

    // ==================== 渠道管理（REQ-20260511-0030）====================
    {
      path: 'channel',
      element: <Outlet />,
      children: [
        { path: 'shops', element: withSuspense(ChannelShopListPage) },
        // 渠道列表（REQ-20260518-0007）
        { path: 'list', element: withSuspense(ChannelListPage) },
      ],
    },

    // ==================== 物流/运费管理 ====================
    {
      path: 'shipping',
      element: <Outlet />,
      children: [
        { path: 'logistics', element: withSuspense(LogisticsCompanyListPage) },
        { path: 'freight-template', element: withSuspense(FreightTemplateListPage) },
      ],
    },

    // ==================== 快递物流管理 (Phase 3.2) ====================
    {
      path: 'logistics',
      element: <Outlet />,
      children: [
        { path: 'shipments', element: withSuspense(ShipmentList) },
        { path: 'shipments/:id', element: withSuspense(ShipmentDetail) },
        { path: 'carriers', element: withSuspense(ShippingCarrierList) },
        { path: 'carrier-permissions', element: withSuspense(CarrierPermissionList) },
      ],
    },

    // ==================== 配送管理 ====================
    {
      path: 'delivery',
      element: <Outlet />,
      children: [
        { path: 'list', element: withSuspense(DeliveryListPage) },
      ],
    },

    // ==================== 报表中心 ====================
    {
      path: 'report',
      element: <Outlet />,
      children: [
        { path: 'order-analysis', element: withSuspense(OrderBusinessAnalysis) },
        // REQ-20260622-0012: 销售报表 → 订单经营分析（升级整合）
        { path: 'sales', element: <Navigate to="/tenant/report/order-analysis" replace /> },
        { path: 'inventory', element: withSuspense(InventoryReportPage) },
        { path: 'finance', element: withSuspense(FinanceReportPage) },
      ],
    },

    // ==================== 业务管理 (客户关系) ====================
    {
      path: 'business',
      element: <Outlet />,
      children: [
        { path: 'customers', element: withSuspense(CustomerManagement) },
      ],
    },

    // ==================== 分销管理 — 重定向到货盘管理 ====================
    {
      path: 'distribution',
      element: <Outlet />,
      children: [
        { path: 'sub-tenant', element: <Navigate to="/tenant/cargo/distributors" replace /> },
        { path: 'application/create', element: <Navigate to="/tenant/cargo/distributors/create" replace /> },
        { path: 'application/:id', element: <Navigate to="/tenant/cargo/distributors/:id" replace /> },
        { path: 'sub-tenant/:id', element: <Navigate to="/tenant/cargo/distributors/:id" replace /> },
        { path: 'products', element: <Navigate to="/tenant/cargo/products" replace /> },
        { path: 'pricing/strategies', element: <Navigate to="/tenant/cargo/pricing" replace /> },
        { path: 'customers', element: <Navigate to="/tenant/customer/list" replace /> },
      ],
    },

    // ==================== 订阅自服务 (模块控制: subscription_self_service) ====================
    {
      path: 'subscription',
      element: <Outlet />,
      children: [
        { path: 'plan', element: withSuspense(SubSelfMyPlanPage) },
        { path: 'orders', element: withSuspense(SubSelfOrderListPage) },
        { path: 'invoices', element: withSuspense(SubSelfInvoiceListPage) },
        { path: 'message-subscriptions', element: withSuspense(MessageSubscriptionsPage) },
      ],
    },

    { path: 'openapi-apps', element: <Navigate to="/tenant/customer/openapi-apps" replace /> },

    // ==================== 小程序管理（业务模式一级导航）====================
    // 选款小程序 = 复用团购管理页面（原在 admin 模式 /tenant/admin/tuangou，
    // 受 AdminModeRoute 网关；迁到业务模式 /tenant/miniprogram/style/* 重挂）。
    // 分销 / 项目 小程序 = 先建骨架占位，页面后续迭代。fnd_menus 迁移见 137。
    {
      path: 'miniprogram',
      children: [
        { index: true, element: <Navigate to="/tenant/miniprogram/style/demands" replace /> },
        // ── 选款小程序（复用团购小程序后台页面）──
        { path: 'style', element: <Navigate to="/tenant/miniprogram/style/demands" replace /> },
        { path: 'style/demands', element: withSuspense(TuangouDemandsPage) },
        { path: 'style/settings', element: withSuspense(TuangouSettingsPage) },
        { path: 'style/packages', element: withSuspense(TuangouPackagesPage) },
        { path: 'style/packages/:id', element: withSuspense(TuangouPackageDetailPage) },
        { path: 'style/promo-skus', element: withSuspense(TuangouPromoSkusPage) },
        { path: 'style/orders', element: withSuspense(TuangouOrdersPage) },
        { path: 'style/customers', element: withSuspense(TuangouCustomersPage) },
        { path: 'style/leads', element: withSuspense(TuangouLeadsPage) },
        { path: 'style/refunds', element: withSuspense(TuangouRefundRequestsPage) },
        { path: 'style/content-blocks', element: withSuspense(TuangouContentBlocksPage) },
        // ── 分销小程序（骨架占位）──
        { path: 'distribution', element: <Navigate to="/tenant/miniprogram/distribution/customers" replace /> },
        { path: 'distribution/customers', element: <MiniProgramPlaceholder title="分销客户" desc="分销小程序的分销客户管理，功能建设中" /> },
        { path: 'distribution/products', element: <MiniProgramPlaceholder title="分销商品" desc="分销小程序的分销商品/货盘管理，功能建设中" /> },
        { path: 'distribution/settings', element: <MiniProgramPlaceholder title="小程序配置" desc="分销小程序的配置（二维码/分享/上架），功能建设中" /> },
        // ── 项目小程序（骨架占位）──
        { path: 'project', element: <Navigate to="/tenant/miniprogram/project/list" replace /> },
        { path: 'project/list', element: <MiniProgramPlaceholder title="推广项目" desc="项目小程序的推广项目管理，功能建设中" /> },
        { path: 'project/signups', element: <MiniProgramPlaceholder title="项目报名" desc="项目小程序的报名/客户管理，功能建设中" /> },
        { path: 'project/settings', element: <MiniProgramPlaceholder title="小程序配置" desc="项目小程序的配置，功能建设中" /> },
      ],
    },

    // ==================== 管理模式 (需要管理模式权限) ====================
    {
      path: 'admin',
      element: (
        <AdminModeRoute>
          <Outlet />
        </AdminModeRoute>
      ),
      children: [
        // 管理概览
        { index: true, element: withSuspense(OverviewPage) },
        { path: 'overview', element: withSuspense(OverviewPage) },
        // dashboard 是管理端默认落地页，保持指向真实 Overview，避免回归
        { path: 'dashboard', element: withSuspense(OverviewPage) },
        // REQ-20260618-0003 M1：森林绿看板（标志性验证页，独立预览路由，
        // 不劫持默认落地；替换 Overview 属 M4）
        { path: 'forest-dashboard', element: withSuspense(ForestDashboardPage) },
        // 租户配置
        { path: 'settings', element: withSuspense(TenantSettingsPage) },
        // 子租户管理
        { path: 'sub-tenants', element: withSuspense(SubTenantsPage) },
        // 订阅与计费
        { path: 'subscription', element: withSuspense(MySubscriptionPage) },
        { path: 'orders', element: withSuspense(MyOrdersPage) },
        { path: 'payment', element: withSuspense(PaymentPage) },
        { path: 'invoices', element: withSuspense(MyInvoicesPage) },
        // 主题配置
        { path: 'theme', element: withSuspense(ThemeConfigPage) },
        // REQ-20260508-0001 #10186: 品牌设置（登录页主题定制）
        { path: 'brand', element: withSuspense(BrandSettingsPage) },
        // T88-1: 团购小程序后台管理
        { path: 'tuangou', element: <Navigate to="/tenant/admin/tuangou/demands" replace /> },
        { path: 'tuangou/packages', element: withSuspense(TuangouPackagesPage) },
        { path: 'tuangou/packages/:id', element: withSuspense(TuangouPackageDetailPage) },
        { path: 'tuangou/settings', element: withSuspense(TuangouSettingsPage) },
        // T88-2: 团购小程序后台管理扩展
        { path: 'tuangou/promo-skus', element: withSuspense(TuangouPromoSkusPage) },
        { path: 'tuangou/orders', element: withSuspense(TuangouOrdersPage) },
        { path: 'tuangou/customers', element: withSuspense(TuangouCustomersPage) },
        { path: 'tuangou/leads', element: withSuspense(TuangouLeadsPage) },
        { path: 'tuangou/refunds', element: withSuspense(TuangouRefundRequestsPage) },
        { path: 'tuangou/content-blocks', element: withSuspense(TuangouContentBlocksPage) },
        // Task D #133: 团购需求管理
        { path: 'tuangou/demands', element: withSuspense(TuangouDemandsPage) },
        // TASK-19 M4: 经销商商城小程序配置
        { path: 'mini-mall/settings', element: withSuspense(MallSettingsPage) },
        // 对外接口管理
        { path: 'openapi-apps', element: withSuspense(OpenapiAppsPage) },
        // 第三方对接配置（出站方向，REQ-20260515-0006）
        { path: 'integration-configs', element: withSuspense(IntegrationConfigsPage) },
        // 聚水潭外部平台对接
        { path: 'external-platform/jushuitan', element: withSuspense(JushuitanPage) },
      ],
    },

    // ==================== 系统管理 ====================
    {
      path: 'system',
      children: [
        // 员工管理 — 业务模式直接访问，不需要管理模式
        { path: 'users', element: withSuspense(UserListPage) },
        { path: 'users/:id', element: withSuspense(UserDetailPage) },
        // 组织人员相关 — 业务模式直接访问
        { path: 'organization', element: withSuspense(OrganizationManagementPage) },
        { path: 'position', element: withSuspense(PositionList) },
        // 以下路由需要管理模式权限（pathless layout route）
        {
          element: (
            <AdminModeRoute>
              <Outlet />
            </AdminModeRoute>
          ),
          children: [
        // 角色管理（客户管理员禁入：只能选用预设角色，不能自建）
        { path: 'roles', element: <CustomerAdminBlockedRoute>{withSuspense(RoleListPage)}</CustomerAdminBlockedRoute> },
        // 权限资源管理（客户管理员禁入）
        { path: 'permissions', element: <CustomerAdminBlockedRoute>{withSuspense(PermissionResourcePage)}</CustomerAdminBlockedRoute> },
        // 菜单管理（客户管理员禁入：不能改菜单）
        { path: 'menus', element: <CustomerAdminBlockedRoute>{withSuspense(MenuManagementPage)}</CustomerAdminBlockedRoute> },
        // 数据权限管理
        { path: 'data-permission', element: withSuspense(DataPermissionConfigPage) },
        { path: 'data-scope', element: withSuspense(DataScopeList) },
        { path: 'field-permission', element: withSuspense(FieldPermissionList) },
        // 日志管理
        { path: 'login-log', element: withSuspense(LoginLogList) },
        { path: 'audit-log', element: withSuspense(AuditLogList) },
        // 权限引擎
        { path: 'permission-override', element: withSuspense(PermissionOverridePage) },
        { path: 'permission-audit', element: withSuspense(PermissionAuditLogPage) },
        // 权限溯源
        { path: 'permission-tracing', element: withSuspense(PermissionTracingPage) },
        // 数据字典
        { path: 'dictionary', element: withSuspense(DataDictionaryPage) },
        // 系统配置 & 通知配置
        { path: 'config', element: withSuspense(SystemConfigPage) },
        { path: 'ln-yima-config', element: withSuspense(LnYimaConfigPage) },
        // 后台任务管理
        { path: 'job', element: withSuspense(JobListPage) },
        { path: 'job/log', element: withSuspense(JobLogListPage) },
        // 原型预览
        { path: 'prototype/security-audit', element: withSuspense(SecurityAuditPrototype) },
        { path: 'prototype/access-control', element: withSuspense(AccessControlPrototype) },
        { path: 'prototype/system-settings', element: withSuspense(SystemSettingsPrototype) },
        { path: 'prototype/organization', element: withSuspense(OrganizationPrototype) },
          ],
        },
      ],
    },

    // ==================== 消息中心 (业务模式) ====================
    {
      path: 'message-center',
      element: withSuspense(MessageCenterPage),
    },
    {
      path: 'admin/announcements',
      element: withSuspense(AnnouncementManagementPage),
    },

    // ==================== 外部平台 - 客户视角 (聚水潭) ====================
    {
      path: 'external-platform/jushuitan',
      element: withSuspense(JstCustomerPage),
    },

    // ==================== 账号设置 ====================
    {
      path: 'account/settings',
      element: withSuspense(AccountSettings),
    },

    // ==================== 个人中心 ====================
    {
      path: 'profile',
      element: withSuspense(Profile),
    },
  ],
};

export default tenantRoutes;
